/**
 * LGL Coding Structure - Canonical Data
 *
 * Source of truth extracted from 14 verified files:
 *   4 Excel templates (Maestro operation codes)
 *   10 PDFs (department codes, cost types, discipline map, project structure, examples)
 *
 * Last verified: Oct 29, 2024 (template date) / Feb 5, 2026 (DOC020526)
 *
 * IMPORTANT: Every value in this file comes directly from a source document.
 * Do NOT add codes, descriptions, or mappings that are not in the source files.
 */

// ─── COST TYPE LETTER DEFINITIONS (from "Job Cost Types.pdf") ───

export const COST_TYPES = {
  L:  { letter: 'L',  name: 'Labour',                    color: '#6f42c1', description: 'Postings from Payroll time sheets for payments to employees' },
  M:  { letter: 'M',  name: 'Material',                  color: '#fd7e14', description: 'AP invoices, stock sheets — steel, pipe, nuts/bolts, safety items (gloves, glasses, helmets), consumables (welding rod, drill bits, cutting discs)' },
  S:  { letter: 'S',  name: 'Subcontracts',              color: '#e83e8c', description: 'Subcontractors on site or equipment with operator (cranes, boom trucks, insulation, scaffolding). Requires liability insurance + WCB clearance' },
  E:  { letter: 'E',  name: 'Outside Rentals',           color: '#20c997', description: 'Items not owned, rented externally — welding machines, forklifts, manlifts, special tools, trailers' },
  LB: { letter: 'LB', name: 'Room & Board',              color: '#0d6efd', description: 'Also includes meals and travel, from Payroll payments to employees' },
  O:  { letter: 'O',  name: 'Site Expense',              color: '#6c757d', description: 'Misc site expenses — office supplies, expense reimbursements, from Accounts Payable' },
  R:  { letter: 'R',  name: 'Revenue',                   color: '#28a745', description: 'Invoice billings to Clients/Customers from Accounts Receivable' },
};

// Also used as cost type "S": Intercompany (USA Jobs only, office use) and Regulatory Inspection Fees
export const COST_TYPE_NOTES = [
  { letter: 'S', context: 'Intercompany', note: 'For USA Jobs only, internal office use' },
  { letter: 'S', context: 'Regulatory Inspection Fees', note: 'X-Rays, TSSA, pipe testing, electrical inspections' },
];

// ─── DISCIPLINE MAP (from "Project Codes - Multi-Discipline Project.pdf") ───

export const DISCIPLINES = [
  { id: 'PIP', name: 'Piping',                              labourRange: '21000-21999', extrasCode: '81000', color: '#0d6efd' },
  { id: 'MWR', name: 'M&R',                                 labourRange: '22000-22999', extrasCode: '82000', color: '#6f42c1' },
  { id: 'ELE', name: 'Electrical',                          labourRange: '23000-23999', extrasCode: '83000', color: '#fd7e14' },
  { id: 'SHM', name: 'Sheet Metal',                         labourRange: '24000-24999', extrasCode: '84000', color: '#20c997' },
  { id: 'PFS', name: 'Pipe Shop Fabrication',               labourRange: '25000-25999', extrasCode: '85000', color: '#e83e8c' },
  { id: 'CIV', name: 'Civil',                               labourRange: '26000-26999', extrasCode: '86000', color: '#795548' },
  { id: 'STR', name: 'Structural',                          labourRange: '27000-27999', extrasCode: '87000', color: '#607D8B' },
  { id: 'TNK', name: 'Tank Fabrication',                     labourRange: '28000-28999', extrasCode: '88000', color: '#dc3545' },
  { id: 'BMT', name: 'Boiler Maker / Field Erected Tanks',  labourRange: '29000-29999', extrasCode: '89000', color: '#17a2b8' },
];

// ─── OPERATION CODES (from 4 Excel Maestro templates) ───
// Type: 'Section' | 'Phase' | 'Activity'
// costFlags: which cost type columns are ACTIVE (not inactive) for this code
// templates: which template MD sheets include this code (1=Large, 2=Small, 3=PipeFab, 4=TankShop)

export const OPERATION_CODES = [
  // ── CONTRACT EXPENSES ──
  { code: '00005', description: 'CONTRACT EXPENSES',              type: 'Phase',    group: 'Contract Expenses',   templates: [1,2,3,4] },
  { code: '00102', description: 'Material',                       type: 'Activity', group: 'Contract Expenses',   templates: [1,2,3,4], costType: 'M' },
  { code: '00103', description: 'Sub-Contracts',                  type: 'Activity', group: 'Contract Expenses',   templates: [1,2,3,4], costType: 'S' },
  { code: '00104', description: 'Rentals',                        type: 'Activity', group: 'Contract Expenses',   templates: [1,2,3],   costType: 'E' },
  { code: '00105', description: 'I/C Activity',                   type: 'Activity', group: 'Contract Expenses',   templates: [1,2,3,4], costType: 'S' },
  { code: '00106', description: 'Regulatory Inspection Fees',     type: 'Activity', group: 'Contract Expenses',   templates: [1,2,3,4], costType: 'S' },
  { code: '00108', description: 'Site Expense',                   type: 'Activity', group: 'Contract Expenses',   templates: [1,2,3,4], costType: 'O' },

  // ── ROOM AND BOARD ──
  { code: '01014', description: 'ROOM AND BOARD',                 type: 'Phase',    group: 'Indirect Labour',     templates: [1,2,3,4] },
  { code: '01015', description: 'Room and Board',                 type: 'Activity', group: 'Indirect Labour',     templates: [1,2,3,4], costType: 'LB' },

  // ── SUPPORT STAFF ──
  { code: '01020', description: 'SUPPORT STAFF',                  type: 'Phase',    group: 'Indirect Labour',     templates: [1,2,3,4] },
  { code: '01030', description: 'Project Manager',                type: 'Activity', group: 'Indirect Labour',     templates: [1,2,3,4], costType: 'L' },
  { code: '01031', description: 'Senior Project Manager Sponsor', type: 'Activity', group: 'Indirect Labour',     templates: [1],       costType: 'L' },
  { code: '01032', description: 'Project Engineer',               type: 'Activity', group: 'Indirect Labour',     templates: [1,3,4],   costType: 'L' },
  { code: '01033', description: 'Sr. Project / Technical Co-Ordinator', type: 'Activity', group: 'Indirect Labour', templates: [1],     costType: 'L' },
  { code: '01034', description: 'Project / Technical Co-Ordinator',     type: 'Activity', group: 'Indirect Labour', templates: [1,2,3,4], costType: 'L' },
  { code: '01035', description: 'Site Clerk or Administrator',    type: 'Activity', group: 'Indirect Labour',     templates: [1,2],     costType: 'L' },
  { code: '01036', description: 'Drafting',                       type: 'Activity', group: 'Indirect Labour',     templates: [1,2,3,4], costType: 'L' },
  { code: '01037', description: 'Shop Quality Control',           type: 'Activity', group: 'Indirect Labour',     templates: [1,3,4],   costType: 'L' },
  { code: '01038', description: 'Field Quality Control',          type: 'Activity', group: 'Indirect Labour',     templates: [1,2],     costType: 'L' },
  { code: '01039', description: 'Safety Services',                type: 'Activity', group: 'Indirect Labour',     templates: [1,2,3,4], costType: 'L' },
  { code: '01040', description: 'Sub-Contract Co-Ordinator',      type: 'Activity', group: 'Indirect Labour',     templates: [1],       costType: 'L' },
  { code: '01041', description: 'Document Control',               type: 'Activity', group: 'Indirect Labour',     templates: [1],       costType: 'L' },
  { code: '01042', description: 'Purchasing',                     type: 'Activity', group: 'Indirect Labour',     templates: [1,2],     costType: 'L' },
  { code: '01043', description: 'Material Receiving / Control',   type: 'Activity', group: 'Indirect Labour',     templates: [1],       costType: 'L' },
  { code: '01044', description: 'Estimator',                      type: 'Activity', group: 'Indirect Labour',     templates: [1],       costType: 'L' },
  { code: '01045', description: 'Scheduler',                      type: 'Activity', group: 'Indirect Labour',     templates: [1],       costType: 'L' },

  // ── SUPERVISORY LABOUR ──
  { code: '01100', description: 'SUPERVISORY LABOUR',             type: 'Phase',    group: 'Indirect Labour',     templates: [1,2,3,4] },
  { code: '01141', description: 'Field Foreman',                  type: 'Activity', group: 'Indirect Labour',     templates: [1,2],     costType: 'L' },
  { code: '01142', description: 'Field General Foreman',          type: 'Activity', group: 'Indirect Labour',     templates: [1],       costType: 'L' },
  { code: '01143', description: 'Shop Supervisor',                type: 'Activity', group: 'Indirect Labour',     templates: [1,3,4],   costType: 'L' },
  { code: '01144', description: 'Shop Foreman',                   type: 'Activity', group: 'Indirect Labour',     templates: [1,3,4],   costType: 'L' },
  { code: '01145', description: 'Field Working Foreman',          type: 'Activity', group: 'Indirect Labour',     templates: [1,2],     costType: 'L' },

  // ── OTHER DIRECT LABOUR ──
  { code: '01200', description: 'OTHER DIRECT LABOUR',            type: 'Phase',    group: 'Indirect Labour',     templates: [1,2,3,4] },
  { code: '01245', description: 'Material Handling Shop',         type: 'Activity', group: 'Indirect Labour',     templates: [1,3,4],   costType: 'L' },
  { code: '01246', description: 'Material Handling Field',        type: 'Activity', group: 'Indirect Labour',     templates: [1,2],     costType: 'L' },
  { code: '01247', description: 'Field Tool Crib Attendant',      type: 'Activity', group: 'Indirect Labour',     templates: [1],       costType: 'L' },
  { code: '01248', description: 'Job Mob/De-Mob',                 type: 'Activity', group: 'Indirect Labour',     templates: [1,2,3,4], costType: 'L' },
  { code: '01250', description: 'Tool Crib / Deliveries',         type: 'Activity', group: 'Indirect Labour',     templates: [1,2,3,4], costType: 'L' },
  { code: '01251', description: 'Prep., Cutting & Cleaning of Pipe', type: 'Activity', group: 'Indirect Labour',  templates: [1,3],     costType: 'L' },
  { code: '01252', description: 'Welder Qualification',           type: 'Activity', group: 'Indirect Labour',     templates: [1,3,4],   costType: 'L' },
  { code: '01253', description: 'Sandblasting & Painting',        type: 'Activity', group: 'Indirect Labour',     templates: [1,3,4],   costType: 'L' },
  { code: '01254', description: 'Testing and Startup',            type: 'Activity', group: 'Indirect Labour',     templates: [1,3],     costType: 'L' },
  { code: '01255', description: 'Fabrication of Pipe Supports',   type: 'Activity', group: 'Indirect Labour',     templates: [1,3],     costType: 'L' },
  { code: '01256', description: 'Job / Shop Clean Up',            type: 'Activity', group: 'Indirect Labour',     templates: [1,2,3,4], costType: 'L' },
  { code: '01257', description: 'Sheet Metal Shop',               type: 'Activity', group: 'Indirect Labour',     templates: [1,2,3,4], costType: 'L' },
  { code: '01258', description: 'Pipe Fabrication Shop',          type: 'Activity', group: 'Indirect Labour',     templates: [1,2,4],   costType: 'L' },
  { code: '01259', description: 'Tank Shop',                      type: 'Activity', group: 'Indirect Labour',     templates: [1,2,3],   costType: 'L' },
  { code: '01260', description: 'Safety Orientation',             type: 'Activity', group: 'Indirect Labour',     templates: [1],       costType: 'L' },
  { code: '01261', description: 'Drug Testing',                   type: 'Activity', group: 'Indirect Labour',     templates: [1],       costType: 'L' },
  { code: '01262', description: 'Runner',                         type: 'Activity', group: 'Indirect Labour',     templates: [1],       costType: 'L' },
  { code: '01263', description: 'Fire Watch',                     type: 'Activity', group: 'Indirect Labour',     templates: [1],       costType: 'L' },

  // ── DIRECT LABOUR (GENERAL) ──
  { code: '02000', description: 'DIRECT LABOUR',                  type: 'Phase',    group: 'Direct Labour',       templates: [1] },
  { code: '02001', description: 'Miscellaneous Labour',           type: 'Activity', group: 'Direct Labour',       templates: [1],       costType: 'L' },

  // ── DISCIPLINE DIRECT LABOUR ──
  { code: '21000', description: 'PIPING DIRECT LABOUR',           type: 'Phase',    group: 'Discipline Labour',   templates: [1,2] },
  { code: '21001', description: '1st Operation Code',             type: 'Activity', group: 'Discipline Labour',   templates: [1,2],     costType: 'L', discipline: 'PIP' },
  { code: '22000', description: 'M&R DIRECT LABOUR',              type: 'Phase',    group: 'Discipline Labour',   templates: [1,2] },
  { code: '22001', description: '1st Operation Code',             type: 'Activity', group: 'Discipline Labour',   templates: [1,2],     costType: 'L', discipline: 'MWR' },
  { code: '23000', description: 'ELECTRICAL DIRECT LABOUR',       type: 'Phase',    group: 'Discipline Labour',   templates: [1,2] },
  { code: '23001', description: '1st Operation Code',             type: 'Activity', group: 'Discipline Labour',   templates: [1,2],     costType: 'L', discipline: 'ELE' },
  { code: '24000', description: 'SHEETMETAL DIRECT LABOUR',       type: 'Phase',    group: 'Discipline Labour',   templates: [1,2] },
  { code: '24001', description: '1st Operation Code',             type: 'Activity', group: 'Discipline Labour',   templates: [1,2],     costType: 'L', discipline: 'SHM' },
  { code: '25000', description: 'PIPE FAB SHOP DIRECT LABOUR',    type: 'Phase',    group: 'Discipline Labour',   templates: [1,2,3] },
  { code: '25001', description: '1st Operation Code',             type: 'Activity', group: 'Discipline Labour',   templates: [1,2,3],   costType: 'L', discipline: 'PFS' },
  { code: '26000', description: 'CIVIL DIRECT LABOUR',            type: 'Phase',    group: 'Discipline Labour',   templates: [1,2] },
  { code: '26001', description: '1st Operation Code',             type: 'Activity', group: 'Discipline Labour',   templates: [1,2],     costType: 'L', discipline: 'CIV' },
  { code: '27000', description: 'STRUCTURAL DIRECT LABOUR',       type: 'Phase',    group: 'Discipline Labour',   templates: [1,2] },
  { code: '27001', description: '1st Operation Code',             type: 'Activity', group: 'Discipline Labour',   templates: [1,2],     costType: 'L', discipline: 'STR' },

  // Tank Fab Shop has detailed activity breakdown
  { code: '28000', description: 'TANK FAB SHOP DIRECT LABOUR',    type: 'Phase',    group: 'Discipline Labour',   templates: [1,4] },
  { code: '28001', description: 'Floor/Cone',                     type: 'Activity', group: 'Discipline Labour',   templates: [1,4],     costType: 'L', discipline: 'TNK' },
  { code: '28002', description: 'Roof/Head',                      type: 'Activity', group: 'Discipline Labour',   templates: [1,4],     costType: 'L', discipline: 'TNK' },
  { code: '28003', description: 'Shell',                          type: 'Activity', group: 'Discipline Labour',   templates: [1,4],     costType: 'L', discipline: 'TNK' },
  { code: '28004', description: 'Nozzles',                        type: 'Activity', group: 'Discipline Labour',   templates: [1,4],     costType: 'L', discipline: 'TNK' },
  { code: '28005', description: 'Internals',                      type: 'Activity', group: 'Discipline Labour',   templates: [1,4],     costType: 'L', discipline: 'TNK' },
  { code: '28006', description: 'Support Structure Ring, Saddles, Anchors', type: 'Activity', group: 'Discipline Labour', templates: [1,4], costType: 'L', discipline: 'TNK' },
  { code: '28007', description: 'Miscellaneous Steel - Lifting Lugs',      type: 'Activity', group: 'Discipline Labour', templates: [1,4], costType: 'L', discipline: 'TNK' },
  { code: '28008', description: 'Plasma Cutting',                 type: 'Activity', group: 'Discipline Labour',   templates: [1,4],     costType: 'L', discipline: 'TNK' },
  { code: '28009', description: 'Prep Shell Plates',              type: 'Activity', group: 'Discipline Labour',   templates: [1,4],     costType: 'L', discipline: 'TNK' },
  { code: '28010', description: 'Roll Shell Plates',              type: 'Activity', group: 'Discipline Labour',   templates: [1,4],     costType: 'L', discipline: 'TNK' },
  { code: '28011', description: 'Hydrotest & Cleaning',           type: 'Activity', group: 'Discipline Labour',   templates: [1,4],     costType: 'L', discipline: 'TNK' },
  { code: '28012', description: '1st Operation Code',             type: 'Activity', group: 'Discipline Labour',   templates: [1,4],     costType: 'L', discipline: 'TNK' },

  { code: '29000', description: 'BM / FIELD ERECTED TANKS',       type: 'Phase',    group: 'Discipline Labour',   templates: [1,2] },
  { code: '29001', description: '1st Operation Code',             type: 'Activity', group: 'Discipline Labour',   templates: [1,2],     costType: 'L', discipline: 'BMT' },

  // ── EXTRAS (per discipline) ──
  { code: '81000', description: 'PIPING EXTRAS',                  type: 'Phase',    group: 'Extras',              templates: [1,2] },
  { code: '82000', description: 'M&R EXTRAS',                     type: 'Phase',    group: 'Extras',              templates: [1,2] },
  { code: '83000', description: 'ELECTRICAL EXTRAS',              type: 'Phase',    group: 'Extras',              templates: [1,2] },
  { code: '84000', description: 'SHEETMETAL EXTRAS',              type: 'Phase',    group: 'Extras',              templates: [1,2] },
  { code: '85000', description: 'PIPE FAB SHOP EXTRAS',           type: 'Phase',    group: 'Extras',              templates: [1,2,3] },
  { code: '86000', description: 'CIVIL EXTRAS',                   type: 'Phase',    group: 'Extras',              templates: [1,2] },
  { code: '87000', description: 'STRUCTURAL EXTRAS',              type: 'Phase',    group: 'Extras',              templates: [1,2] },
  { code: '88000', description: 'TANK SHOP EXTRAS',               type: 'Phase',    group: 'Extras',              templates: [1,2,4] },
  { code: '89000', description: 'BM / FIELD ERECTED TANK EXTRAS', type: 'Phase',    group: 'Extras',              templates: [1,2] },

  // ── USA INTERCOMPANY ──
  { code: '85001', description: 'I/C INTERIM RECONCILIATIONS',    type: 'Section',  group: 'USA Intercompany',    templates: [1,2,3] },
  { code: '85009', description: 'MATERIAL COSTS',                 type: 'Phase',    group: 'USA Intercompany',    templates: [1,2,3] },
  { code: '85102', description: 'Material',                       type: 'Activity', group: 'USA Intercompany',    templates: [1,2,3],   costType: 'O' },
  { code: '85120', description: 'LABOUR COSTS',                   type: 'Phase',    group: 'USA Intercompany',    templates: [1,2,3] },
  { code: '85125', description: 'Labour',                         type: 'Activity', group: 'USA Intercompany',    templates: [1,2,3],   costType: 'L' },

  // ── REVENUES ──
  { code: '90000', description: 'REVENUES',                       type: 'Phase',    group: 'Revenues',            templates: [1,2,3,4] },
  { code: '90001', description: 'Revenue',                        type: 'Activity', group: 'Revenues',            templates: [1,2,3,4], costType: 'R' },
];

// ─── T&M ACTIVITY CODES (from "Project Breakdown - T&M Project.pdf") ───

export const TM_CODES = [
  { code: '00102', description: 'Material',                              costType: 'M' },
  { code: '00103', description: 'Subcontractor',                         costType: 'S' },
  { code: '00104', description: 'Outside Rentals',                       costType: 'E' },
  { code: '00105', description: 'I/C Activity (Internal Office Use Only)', costType: 'S' },
  { code: '00106', description: 'Regulatory Inspection Fees',            costType: 'S' },
  { code: '00108', description: 'Site Expenses',                         costType: 'O' },
  { code: '01015', description: 'Room & Board; Travel; Meals',           costType: 'LB' },
  { code: '02001', description: 'Labour',                                costType: 'L' },
  { code: '90001', description: 'Revenue',                               costType: 'R' },
];

// ─── TEMPLATE DEFINITIONS ───

export const TEMPLATES = [
  {
    id: 1,
    name: 'Large Multi-Discipline',
    file: '1 Maestro NEW Large Multi Discipline Template.xlsx',
    shortName: 'Large MD',
    description: 'All 9 disciplines + full support staff (96 codes)',
    sheets: ['MD=MASTER', 'T&M Jobs', 'USA Intercompany'],
  },
  {
    id: 2,
    name: 'Small & Simple Multi-Discipline',
    file: '2 Maestro NEW Small & Simple Multi Discipline Template.xlsx',
    shortName: 'Small MD',
    description: 'Subset of Large — fewer support staff (58 codes)',
    sheets: ['MD=MASTER', 'T&M Jobs', 'USA Intercompany'],
  },
  {
    id: 3,
    name: 'Pipe Fab Shop',
    file: '3 Maestro NEW PIPE FAB SHOP Template.xlsx',
    shortName: 'Pipe Fab',
    description: 'Pipe fabrication shop discipline only (39 codes)',
    sheets: ['MD=MASTER', 'T&M Jobs', 'USA Intercompany'],
  },
  {
    id: 4,
    name: 'Tank Shop',
    file: '4 Maestro NEW TANK SHOP Template.xlsx',
    shortName: 'Tank Shop',
    description: 'Tank fabrication shop with detailed activities (46 codes)',
    sheets: ['MD=MASTER'],
  },
];

// ─── DEPARTMENT CODES (from "Department Codes - Canadian Sales.pdf" + "US Sales.pdf") ───
// Format: Country letter + 2-letter region + customer/description

export const DEPARTMENT_CODES = {
  canadian: {
    title: 'Canadian Sales',
    prefix: 'C',
    regionLabel: 'Province',
    format: 'C + XX (province) + Customer Name',
    codes: [
      { code: 'CONBASF',           region: 'ON', customer: 'BASF Canada Inc.' },
      { code: 'CONGREENFIELD',     region: 'ON', customer: 'Greenfield Global Inc.' },
      { code: 'CONIVACO',          region: 'ON', customer: 'Ivaco Rolling Mills' },
      { code: 'CONINVISTA',        region: 'ON', customer: 'Invista Canada' },
      { code: 'CONNEMASKA',        region: 'ON', customer: 'Nemaska Lithium Inc.' },
      { code: 'CONOMYA',           region: 'ON', customer: 'Omya Canada Inc.' },
      { code: 'CONP&G',            region: 'ON', customer: 'P&G Proctor & Gamble' },
      { code: 'CON2412816',        region: 'ON', customer: '2412816 Ontario Inc.' },
      { code: 'CABOTHERINDUSTRY',  region: 'AB', customer: 'Other Industry — Alberta' },
      { code: 'CBCOTHERINDUSTRY',  region: 'BC', customer: 'Other Industry — British Columbia' },
      { code: 'CMBOTHERINDUSTRY',  region: 'MB', customer: 'Other Industry — Manitoba' },
      { code: 'CNFOTHERINDUSTRY',  region: 'NF', customer: 'Other Industry — Newfoundland & Labrador' },
      { code: 'CONOTHERINDUSTRY',  region: 'ON', customer: 'Other Industry — Ontario' },
      { code: 'CSKOTHERINDUSTRY',  region: 'SK', customer: 'Other Industry — Saskatchewan' },
      { code: 'CONOVERHEAD',       region: 'ON', customer: 'Canadian Overhead' },
    ]
  },
  us: {
    title: 'US Sales',
    prefix: 'U',
    regionLabel: 'State',
    format: 'U + XX (state) + Customer Name',
    codes: [
      { code: 'UFLOTHERINDUSTRY',  region: 'FL', customer: 'Other Industry — Florida' },
      { code: 'UGAOTHERINDUSTRY',  region: 'GA', customer: 'Other Industry — Georgia' },
      { code: 'UINOTHERINDUSTRY',  region: 'IN', customer: 'Other Industry — Indiana' },
      { code: 'UILOTHERINDUSTRY',  region: 'IL', customer: 'Other Industry — Illinois' },
      { code: 'UKYOTHERINDUSTRY',  region: 'KY', customer: 'Other Industry — Kentucky' },
      { code: 'UNYWESTROCK',       region: 'NY', customer: 'WestRock' },
      { code: 'UNYOTHERINDUSTRY',  region: 'NY', customer: 'Other Industry — New York' },
      { code: 'UNCOTHERINDUSTRY',  region: 'NC', customer: 'Other Industry — North Carolina' },
      { code: 'USCOTHERINDUSTRY',  region: 'SC', customer: 'Other Industry — South Carolina' },
      { code: 'UTNOTHERINDUSTRY',  region: 'TN', customer: 'Other Industry — Tennessee' },
      { code: 'UTXOTHERINDUSTRY',  region: 'TX', customer: 'Other Industry — Texas' },
      { code: 'UVAOTHERINDUSTRY',  region: 'VA', customer: 'Other Industry — Virginia' },
      { code: 'UWVOTHERINDUSTRY',  region: 'WV', customer: 'Other Industry — West Virginia' },
    ]
  }
};

// ─── PROJECT EXAMPLES (from 3 example PDFs) ───

export const PROJECT_EXAMPLES = [
  {
    id: 'iko',
    projectNumber: '71665',
    clientCode: 'IKO101',
    clientName: 'IKO Industries',
    projectName: 'Oxidizer Phase 2 Project',
    type: 'Multi-Discipline',
    disciplines: ['PIP', 'MWR', 'ELE', 'SHM', 'PFS', 'TNK'],
  },
  {
    id: 'scse',
    projectNumber: '75891',
    clientCode: 'SCSE01',
    clientName: 'SCS Energy',
    projectName: 'Ridge Landfill Facility',
    type: 'Multi-Discipline',
    disciplines: ['PIP', 'MWR', 'ELE', 'SHM', 'CIV', 'PFS'],
  },
  {
    id: 'nema',
    projectNumber: '71705',
    clientCode: 'NEMA01',
    clientName: 'Nemaska Lithium Inc.',
    projectName: 'Pipe Spooling',
    type: 'Single Discipline',
    disciplines: ['PFS'],
  },
];

// ─── CODE GROUP DEFINITIONS (for UI grouping) ───

export const CODE_GROUPS = [
  { id: 'Contract Expenses',   range: '00xxx',       color: '#dc3545', icon: '📋' },
  { id: 'Indirect Labour',     range: '01xxx',       color: '#6f42c1', icon: '👔' },
  { id: 'Direct Labour',       range: '02xxx',       color: '#0d6efd', icon: '👷' },
  { id: 'Discipline Labour',   range: '21xxx–29xxx', color: '#28a745', icon: '🔧' },
  { id: 'Extras',              range: '81xxx–89xxx', color: '#fd7e14', icon: '➕' },
  { id: 'USA Intercompany',    range: '85xxx',       color: '#17a2b8', icon: '🇺🇸' },
  { id: 'Revenues',            range: '90xxx',       color: '#28a745', icon: '💰' },
];

// ─── HELPER FUNCTIONS ───

export function getCodesForTemplate(templateId) {
  return OPERATION_CODES.filter(c => c.templates.includes(templateId));
}

export function getCodesByGroup(groupId) {
  return OPERATION_CODES.filter(c => c.group === groupId);
}

export function getDisciplineByAcronym(acronym) {
  return DISCIPLINES.find(d => d.id === acronym);
}
